import type { InsightsOverview, LibraryReport, ReportScope, TableId } from '@myrewards/contracts';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  useCallback,
  useDeferredValue,
  useId,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Label, Link, RadioButton, RadioField, RadioGroup } from 'react-aria-components';
import { useSearchParams } from 'react-router';
import { meQuery } from '../api';
import { overviewQuery, type Loose } from '../api/insights';
import { LevelTwoTabs, type HeroAction, type LevelTwoItem } from '../shell';
import {
  buttonClassName,
  CardHelp,
  cx,
  Icon,
  type CardHelpContent,
  type ScopedLabel,
  useScrollRegion,
  usePaging,
  type Paging,
  HelpShownTag,
  useHelpAnchor,
} from '../ui';
import { canOpen, SECTIONS, SECTOR_SECTIONS, sectorsOnly } from './labels';
import s from './Insights.module.css';

/** The report library the server built for this reader (it decides the tabs and the cards). */
export function useLibrary(): readonly LibraryReport[] {
  const { data } = useSuspenseQuery(overviewQuery());
  return data.library;
}

/** "Ask the data" for a hero, while an Admin has it switched on for this reader; else nothing. */
export function askTheData(library: readonly LibraryReport[]): HeroAction | null {
  return library.some((r) => r.id === 'ask_data' && r.available)
    ? { label: 'Ask the data', icon: 'sparkle', href: '/insights/ask' }
    : null;
}

/** The overview as cached by every Insights page (the tabs come from its library). */
export function useOverviewDefault(): InsightsOverview {
  return useSuspenseQuery(overviewQuery()).data;
}

/** True while this person is previewing other roles: Insights shows no live data then. */
export function usePreviewing(): boolean {
  const { data: me } = useSuspenseQuery(meQuery);
  return me.preview !== null;
}

/** The Insights sections this reader may open, in their fixed order, as Level 2 items. */
export function sectionsFor(
  library: readonly LibraryReport[],
  scope?: ReportScope,
): LevelTwoItem[] {
  // NAV1 (A47): a reader of their own sectors only gets the board's three tabs.
  if (scope !== undefined && sectorsOnly(scope))
    return SECTOR_SECTIONS.filter((x) =>
      'whenOn' in x
        ? library.some((r) => r.id === x.report && r.available)
        : canOpen(library, x.report),
    ).map((x) => ({
      id: x.id,
      label: x.label,
      href: x.href,
      ...('also' in x && { also: x.also }),
      ...('icon' in x && { icon: x.icon }),
    }));
  return SECTIONS.filter((x) => {
    if (x.report === null) return true;
    if ('whenOn' in x) return library.some((r) => r.id === x.report && r.available);
    return canOpen(library, x.report);
  }).map((x) => ({
    id: x.id,
    label: x.label,
    href: x.href,
    end: x.id === 'overview',
    ...('icon' in x && { icon: x.icon }),
  }));
}

/** Level 2: the Insights sections this reader may open, in their fixed order. */
export function InsightsTabs({ actions }: { actions?: ReactNode }): ReactElement | null {
  const { library, scope } = useOverviewDefault();
  return (
    <LevelTwoTabs label="Insights sections" items={sectionsFor(library, scope)} actions={actions} />
  );
}

/**
 * A view kept in the address: read it with the API's own parser, change it by writing a new
 * address (so Back works and a view can be shared). Changing a filter starts again from the first
 * page, because a page number belongs to the view it came from.
 */
export function useView<V>(
  parse: (search: URLSearchParams) => V,
  serialize: (view: V) => string,
): [V, (patch: Loose<V>, options?: { keepPage?: boolean }) => void] {
  const [search, setSearch] = useSearchParams();
  const view = useMemo(() => parse(search), [parse, search]);
  const update = useCallback(
    (patch: Loose<V>, options: { keepPage?: boolean } = {}) => {
      const next = {
        ...view,
        ...patch,
        ...(options.keepPage === true ? {} : { page: undefined }),
      } as V;
      setSearch(new URLSearchParams(serialize(next)), { preventScrollReset: true });
    },
    [view, serialize, setSearch],
  );
  return [view, update];
}

/**
 * A paged report's page and size (the address, then the person's saved choice). A new page loads
 * behind the one showing, so the table never blinks away; `pending` is true meanwhile.
 */
export function useReportPaging<V extends object>(
  table: TableId,
  view: V,
): { paging: Paging; asked: V & { page: number; size: number }; pending: boolean } {
  const paging = usePaging(table);
  const wanted = useMemo(
    () => ({ ...view, page: paging.page, size: paging.size }),
    [view, paging.page, paging.size],
  );
  const asked = useDeferredValue(wanted);
  return { paging, asked, pending: asked !== wanted };
}

// --- KPI tiles ------------------------------------------------------------------------------------

export interface KpiTileProps {
  /** Always scoped: "Company · people recognized, last 12 months". */
  label: ScopedLabel;
  value: string;
  /** Small words after the value ("$49,500", "people"). */
  unit?: string;
  note?: ReactNode;
  /** A thin meter under the value (a share, 0–100). */
  meter?: { percent: number; label: string } | null;
  link?: { label: string; href: string } | null;
  help: CardHelpContent;
  muted?: boolean;
}

/** A KPI with its scope, value, one line of context and where to go next. */
export function KpiTile({
  label,
  value,
  unit,
  note,
  meter,
  link,
  help,
  muted = false,
}: KpiTileProps): ReactElement {
  const id = useId();
  return (
    <section aria-labelledby={id} className={cx(s.kpi, muted && s.kpiMuted)}>
      <div className={s.kpiHead}>
        <h3 id={id} className={cx('eyebrow', s.kpiLabel)}>
          {label}
        </h3>
        <CardHelp topic={label} shows={help.shows} canDo={help.canDo} />
      </div>
      <p className={s.kpiValueRow}>
        <span className={cx('disp', s.kpiValue)}>{value}</span>
        {unit !== undefined && <span className={s.kpiUnit}>{unit}</span>}
      </p>
      {meter !== undefined && meter !== null && (
        <span className={s.meter} role="img" aria-label={meter.label}>
          <span
            className={s.meterFill}
            style={{ width: `${String(Math.max(0, Math.min(100, meter.percent)))}%` }}
          />
        </span>
      )}
      {note !== undefined && <p className={s.kpiNote}>{note}</p>}
      {link !== undefined && link !== null && (
        <Link href={link.href} className={s.kpiLink}>
          {link.label}
        </Link>
      )}
    </section>
  );
}

// --- Controls -------------------------------------------------------------------------------------

export interface SegmentOption<T extends string> {
  id: T;
  label: string;
}

/** A labelled one-of-several choice drawn as joined segments (real radios: arrows move along). */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  hideLabel = false,
}: {
  label: string;
  value: T;
  options: readonly SegmentOption<T>[];
  onChange: (value: T) => void;
  hideLabel?: boolean;
}): ReactElement {
  return (
    <RadioGroup
      className={s.segmented}
      orientation="horizontal"
      value={value}
      onChange={(next) => {
        const found = options.find((o) => o.id === next);
        if (found) onChange(found.id);
      }}
      {...(hideLabel && { 'aria-label': label })}
    >
      {!hideLabel && <Label className={cx('eyebrow', s.controlLabel)}>{label}</Label>}
      <div className={s.segments}>
        {options.map((o) => (
          <RadioField key={o.id} value={o.id}>
            <RadioButton className={s.segment}>{o.label}</RadioButton>
          </RadioField>
        ))}
      </div>
    </RadioGroup>
  );
}

/**
 * A download as a plain link: the browser saves the file (the app's router never handles it). In
 * Preview there's no live data to export, so it says why instead.
 */
export function ExportLink({
  href,
  label,
  rows,
  previewing,
  variant = 'navy',
  empty = null,
}: {
  href: string;
  label: string;
  /** "74 rows", as the file will hold. */
  rows: string;
  previewing: boolean;
  variant?: 'navy' | 'ghost' | 'cyan';
  /** When the file would hold no rows: what it will hold and when, said instead of a download. */
  empty?: string | null;
}): ReactElement {
  // Help's "Show me" points here (APP-090 §8): the download, or why there's none (Preview, or
  // nothing in the view yet).
  const anchor = useHelpAnchor('insights-export');
  if (previewing)
    return (
      <p className={s.exportOff} {...anchor.props}>
        Exports are off in Preview. Preview shows sample screens and never live records, so there’s
        nothing to download. <HelpShownTag shown={anchor.shown} />
      </p>
    );
  if (empty !== null)
    return (
      <p className={s.exportOff} {...anchor.props}>
        {empty} <HelpShownTag shown={anchor.shown} />
      </p>
    );
  return (
    <span className={s.exportWrap} {...anchor.props}>
      <a href={href} download className={buttonClassName({ variant, size: 'sm' })}>
        <Icon name="download" size={16} />
        {label}
      </a>
      <span className={s.exportRows}>{rows}</span>
      <HelpShownTag shown={anchor.shown} />
    </span>
  );
}

/** The thin band of filters above a report ("This page shows"). */
export function FilterBar({
  title = 'This page shows',
  help,
  children,
  note,
}: {
  title?: string;
  help: CardHelpContent;
  children: ReactNode;
  note?: ReactNode;
}): ReactElement {
  const id = useId();
  const filters = useHelpAnchor('insights-filters');
  return (
    <section className={s.filterBar} aria-labelledby={id} {...filters.props}>
      <HelpShownTag shown={filters.shown} placement="edge" />
      <div className={s.filterHead}>
        <h2 id={id} className={cx('eyebrow', s.filterTitle)}>
          {title}
        </h2>
        <CardHelp topic={title} shows={help.shows} canDo={help.canDo} />
      </div>
      <div className={s.filterRow}>{children}</div>
      {note !== undefined && <p className={s.filterNote}>{note}</p>}
    </section>
  );
}

/** A wide table scrolls sideways inside its card, never the page (and can be scrolled by keyboard). */
export function ScrollArea({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const region = useScrollRegion(ref, label);
  return (
    <div ref={ref} className={s.scroll} {...region}>
      {children}
    </div>
  );
}

/**
 * Takes the reader to a card on the page, keyboard focus included: the card's heading takes focus
 * (without a second scroll), so the next Tab continues from there.
 */
export function jumpTo(id: string): void {
  const card = document.getElementById(id);
  if (card === null) return;
  card.scrollIntoView({ block: 'start' });
  const heading = card.querySelector<HTMLElement>('h2, h3');
  if (heading !== null) {
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }
}

/** A sentence that sums up the page, with a chart mark in front (the pace callout). */
export function Callout({ children }: { children: ReactNode }): ReactElement {
  return (
    <p className={s.callout}>
      <Icon name="chart" size={18} />
      <span>{children}</span>
    </p>
  );
}
